import { ContainerFolderRow } from "@/domains/documents/components/organisms/ContainerFolderRow";
import { DocumentRowContextMenu } from "@/domains/documents/components/organisms/DocumentRowContextMenu";
import { SignedContainerRow } from "@/domains/documents/components/organisms/SignedContainerRow";
import { isSignedContainerFile } from "@/domains/documents/constants/fileTypes";
import { cn } from "@/lib/utils";
import type { VirtualItem } from "@tanstack/react-virtual";
import { observer } from "mobx-react-lite";
import type { DocumentTablePresentation } from "./documentTablePresentation";
import type { DocumentWithTags } from "./documentTableTypes";
import type { FlattenedItem } from "./useTableRows";
export const TableContainerDrag = observer(function TableContainerDrag(
  props: DocumentTablePresentation & {
    containerUnifiedIndex: number | undefined;
    containerDoc: DocumentWithTags;
  }
) {
  const {
    containerUnifiedIndex,
    handleRowClickCapture,
    containerDoc,
    onDropDocument,
    isRestore,
    startMoveDrag,
    endMoveDrag,
    dragSourceKey,
  } = props;
  const canDrag = !!onDropDocument && !isRestore;
  return (
    <div
      onClickCapture={
        containerUnifiedIndex === undefined
          ? undefined
          : handleRowClickCapture(containerDoc.id, containerUnifiedIndex)
      }
      draggable={canDrag}
      onDragStart={
        canDrag
          ? startMoveDrag({
              kind: "document",
              id: containerDoc.id,
              label: containerDoc.filename,
            })
          : undefined
      }
      onDragEnd={canDrag ? endMoveDrag : undefined}
      className={cn(
        canDrag && "cursor-grab active:cursor-grabbing",
        dragSourceKey === `document:${containerDoc.id}` && "opacity-50"
      )}
    >
      {isSignedContainerFile(containerDoc.filename) ? (
        // A signed container is a sealed file, not a folder:
        // its title opens the signature panel.
        <TableSignedContainer {...props} containerDoc={containerDoc} />
      ) : (
        <TableArchiveContainer {...props} containerDoc={containerDoc} />
      )}
    </div>
  );
});

export const TableSignedContainer = observer(function TableSignedContainer(
  props: DocumentTablePresentation & { containerDoc: DocumentWithTags }
) {
  const {
    containerDoc,
    showDateCreated,
    showUser,
    uploaderInDateHint,
    showVersion,
    showStatus,
    showTags,
    showParent,
    store,
    showCheckbox,
    handleSelectDocument,
    showActions,
    deleteLabel,
    isRestore,
    handleDownloadDocument,
    canMoveToTrash,
    handleDeleteDocument,
  } = props;
  return (
    <SignedContainerRow
      document={{
        id: containerDoc.id,
        filename: containerDoc.filename,
        size: containerDoc.size,
        createdAt: containerDoc.created_at,
        originalCreatedAt: containerDoc.original_created_at,
        contentModifiedAt: containerDoc.document_content_modified_date,
        createdBy: containerDoc.created_by,
        createdByName: containerDoc.created_by_name,
        createdByAvatarUrl: containerDoc.created_by_avatar_url,
        createdByColor: containerDoc.created_by_color,
        state: containerDoc.state,
      }}
      columns={{ showDateCreated, showUser, showVersion, showStatus, showTags, showParent }}
      uploaderInDateHint={uploaderInDateHint}
      selection={{
        isSelected: store?.isSelected(containerDoc.id) ?? false,
        showCheckbox,
        onSelect: (isSelected) => handleSelectDocument(containerDoc.id, isSelected),
      }}
      actions={{
        showActions,
        deleteLabel,
        isRestore,
        onDownload: () => handleDownloadDocument(containerDoc.id),
        onDelete: canMoveToTrash ? () => handleDeleteDocument(containerDoc.id) : undefined,
      }}
    />
  );
});

export const TableArchiveContainer = observer(function TableArchiveContainer(
  props: DocumentTablePresentation & { containerDoc: DocumentWithTags }
) {
  const {
    containerDoc,
    containerChildCounts,
    showDateCreated,
    showUser,
    uploaderInDateHint,
    showVersion,
    showStatus,
    showTags,
    showParent,
    store,
    showCheckbox,
    showActions,
    deleteLabel,
    isRestore,
    handleSelectDocument,
    onContainerClick,
    handleDownloadDocument,
    canMoveToTrash,
    handleDeleteDocument,
  } = props;
  return (
    <ContainerFolderRow
      filename={containerDoc.filename}
      childCount={
        containerChildCounts === null ? undefined : (containerChildCounts.get(containerDoc.id) ?? 0)
      }
      state={containerDoc.state}
      document={containerDoc}
      columns={{ showDateCreated, showUser, showVersion, showStatus, showTags, showParent }}
      uploaderInDateHint={uploaderInDateHint}
      isSelected={store?.isSelected(containerDoc.id) ?? false}
      showCheckbox={showCheckbox}
      showActions={showActions}
      deleteLabel={deleteLabel}
      isRestore={isRestore}
      onSelect={(isSelected) => handleSelectDocument(containerDoc.id, isSelected)}
      onClick={() => onContainerClick?.(containerDoc.id, containerDoc.filename)}
      onDownload={() => handleDownloadDocument(containerDoc.id)}
      onDelete={canMoveToTrash ? () => handleDeleteDocument(containerDoc.id) : undefined}
    />
  );
});

export const TableContainerRow = observer(function TableContainerRow(
  props: DocumentTablePresentation & { item: FlattenedItem; virtualRow: VirtualItem }
) {
  const {
    item,
    unifiedIndexById,
    virtualRow,
    store,
    handleDownloadDocument,
    canMoveToTrash,
    handleDeleteDocument,
    measureVirtualRow,
  } = props;
  if (item.kind !== "container") return null;

  const containerDoc = item.doc;
  // Same id lookup the folder and document rows use. A
  // container row is a selectable item like any other, so it
  // must be able to SET the anchor as well as be swept into a
  // range — otherwise shift-click behaves differently
  // depending on which row the user clicked first.
  const containerUnifiedIndex = unifiedIndexById.get(`document:${containerDoc.id}`);
  return (
    // Container files render as folder-like rows, so right-click
    // gets the same menu treatment as real folder/document rows.
    // A container is a document — its menu mirrors the row's own
    // 3-dots (ContainerFolderRow's DocumentActions: Download +
    // Delete). Store-less contexts (public share) disable it so
    // the native menu passes through, matching the document rows.
    <DocumentRowContextMenu
      key={`${virtualRow.key}-container-${containerDoc.id}`}
      variant="document"
      disabled={!store}
      isMulti={false}
      selectedCount={1}
      isDwg={false}
      canCompare={false}
      onDownload={() => handleDownloadDocument(containerDoc.id)}
      onDelete={canMoveToTrash ? () => handleDeleteDocument(containerDoc.id) : undefined}
    >
      <div
        data-index={virtualRow.index}
        ref={measureVirtualRow}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          transform: `translateY(${virtualRow.start}px)`,
        }}
      >
        {/* `draggable` stays on its own node rather than the
                              measured one: the measured node is what the
                              virtualizer sizes, and the drag image is taken from
                              the dragged element. The click capture rides here
                              too: both container row components mark their title
                              with `data-row-title`, so a plain click still opens
                              the container or its signature panel. */}
        <TableContainerDrag
          {...props}
          containerUnifiedIndex={containerUnifiedIndex}
          containerDoc={containerDoc}
        />
      </div>
    </DocumentRowContextMenu>
  );
});
